/* eslint-disable */
/**
 * Standard keyboard key code strings for cross-browser keyboard event handling.
 *
 * @remarks
 * Contains string constants representing keyboard keys in a standardized format.
 * These values are used to identify keyboard events in a consistent manner across different browsers.
 */
export enum KEY_CODES {
  UP = 'ArrowUp',
  DOWN = 'ArrowDown',
  ENTER = 'Enter',
  SPACE = ' ',
  TAB = 'Tab',
  BACKSPACE = 'Backspace',
  DELETE = 'Delete',
  ESCAPE = 'Escape',
  X = 'x',
  V = 'v',
  LEFT = 'ArrowLeft',
  RIGHT = 'ArrowRight',
}

/**
 * Maps legacy keyboard event key codes to their modern {@link KEY_CODES} string equivalents.
 *
 * @remarks
 * Provides backward compatibility for older browsers that still use numeric key codes
 * instead of the modern {@link KeyboardEvent.code} property. This mapping translates the
 * deprecated `keyCode` property to the corresponding {@link KEY_CODES} enum value.
 */
export const KEY_CODE_TO_CODE = {
  9: KEY_CODES.TAB,
  38: KEY_CODES.UP,
  40: KEY_CODES.DOWN,
  32: KEY_CODES.SPACE,
  13: KEY_CODES.ENTER,
  46: KEY_CODES.DELETE,
  8: KEY_CODES.BACKSPACE,
  27: KEY_CODES.ESCAPE,
  88: KEY_CODES.X,
  86: KEY_CODES.V,
} as any;

/**
 * Detects whether the browser is Internet Explorer or Microsoft Edge (legacy).
 *
 * @returns `true` if the browser is IE or legacy Edge, `false` otherwise
 *
 * @remarks
 * This utility is used to determine whether to use legacy `keyCode` property
 * or the modern `code` property when handling keyboard events.
 */
export function browserIsMsie() {
  return navigator.userAgent.indexOf('MSIE') > -1 || navigator.userAgent.indexOf('Trident/') > -1;
}

/**
 * Extracts the key code string from a keyboard event in a cross-browser compatible manner.
 *
 * @param event - The keyboard event to extract the key code from
 * @returns The {@link KEY_CODES} string value representing the key pressed
 *
 * @remarks
 * Automatically detects the browser type and uses the appropriate method:
 * - For IE/legacy Edge: converts numeric `keyCode` to {@link KEY_CODES} string
 * - For modern browsers: uses the standard `code` property
 */
export function getKeyCode(event: KeyboardEvent): KEY_CODES {
  // NOTE: detect IE
  if (browserIsMsie()) {
    return KEY_CODE_TO_CODE[event.keyCode] as KEY_CODES;
  }

  return event.code as KEY_CODES;
}
